import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, SyntheticEvent, useCallback, useState } from 'react';

import { imagePlaceholder } from '@/utils/urls';

type Props = {
    alt: string;
    className?: string;
    lazyLoadImage?: boolean;
    srcPrimary: string;
    srcSecondary: string;
};

const ImageWithHoverComponent = ({
    alt = '',
    className = '',
    lazyLoadImage = true,
    srcPrimary = '',
    srcSecondary = '',
    ...rest
}: Props) => {
    const [hover, setHover] = useState(false);
    const [primaryError, setPrimaryError] = useState(false);
    const [secondaryError, setSecondaryError] = useState(false);

    const handleMouseEnter = useCallback(() => {
        setHover(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHover(false);
    }, []);

    const handleImageError = useCallback((e: SyntheticEvent<HTMLImageElement>, imageType: 'primary' | 'secondary') => {
        const target = e.target as HTMLImageElement;
        if (target.src !== imagePlaceholder) {
            target.src = imagePlaceholder;
        }
        if (imageType === 'primary') {
            setPrimaryError(true);
        } else {
            setSecondaryError(true);
        }
    }, []);

    let source = srcPrimary ? srcPrimary : srcSecondary;
    let showSingleImage = false;

    if (primaryError) {
        if (!secondaryError) {
            showSingleImage = true;
            source = srcSecondary;
        }
    }

    if (secondaryError) {
        if (!primaryError) {
            showSingleImage = true;
            source = srcPrimary;
        }
    }

    if (showSingleImage) {
        return (
            <div className={className}>
                <img
                    alt={alt}
                    className="absolute inset-0 m-auto block max-h-full max-w-full object-contain print:m-0"
                    onError={(event: SyntheticEvent<HTMLImageElement>) => {
                        const target = event.target as HTMLImageElement;
                        if (target.src !== imagePlaceholder) {
                            target.src = imagePlaceholder;
                        }
                    }}
                    src={source}
                />
            </div>
        );
    }

    const loading = lazyLoadImage ? 'lazy' : 'eager';
    const usePrimaryStyle = Boolean(srcSecondary) && !secondaryError;

    return (
        <div
            className={className}
            data-testid="imageWithHover"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                className={cn(
                    'opacity-0',
                    hover && 'opacity-100 transition-opacity delay-300 duration-[600ms] ease-in-out'
                )}
            >
                {hover && (
                    <img
                        alt={alt}
                        className="absolute inset-0 m-auto block max-h-full max-w-full object-contain print:m-0"
                        loading={loading}
                        onError={(e) => handleImageError(e, 'secondary')}
                        src={srcSecondary}
                        {...rest}
                    />
                )}
            </div>
            <img
                alt={alt}
                className={cn(
                    'absolute inset-0 m-auto block max-h-full max-w-full object-contain print:m-0',
                    usePrimaryStyle
                        ? 'hover:opacity-0 hover:transition-opacity hover:delay-300 hover:duration-[800ms] hover:ease-in-out'
                        : 'hover:opacity-100 hover:transition-none'
                )}
                loading={loading}
                onError={(e) => handleImageError(e, 'primary')}
                src={source}
                {...rest}
            />
        </div>
    );
};

export const ImageWithHover = memo<Props>(ImageWithHoverComponent);
